/**
 * Where a segment's words sit on the wheel (in the wheel's SVG units, radius 90), as on a printed
 * prize wheel: Treat or Trick across the slice near the rim, and the prize in capitals along the
 * slice's centre line from there towards the hub, on two lines when that reads bigger. Everything
 * is sized to stay inside the slice, checked where it is narrowest.
 */

/** Where Treat or Trick is centred, out near the rim; the prize runs in from it as far as this. */
export const WORD_C = 78
export const TEXT_IN = 27
const WORD_MAX = 10
// The word is tracked out (0.1em), so its letters are wider than the prize's.
const WORD_EM = 0.62

// Average advance per capital, in ems, rounded up so real text is never wider than planned
// (Anton with a little tracking).
const EM = 0.52
const LINE_GAP = 1.12
/** Share of the slice's width the text may fill. */
const FILL = 0.8

export type SliceLayout = {
  /** Treat or Trick, across the slice. */
  word: { size: number }
  lines: string[]
  size: number
  /** Each line's centre, across the slice, from its centre line. */
  lineY: number[]
  /** Where the prize starts (by Treat or Trick) and ends (towards the hub). */
  out: number
  inner: number
}

/** Two lines split at the space nearest the middle, or null for one word. */
function halves(text: string): [string, string] | null {
  const spaces = [...text.matchAll(/ /g)].map((m) => m.index)
  if (!spaces.length) return null
  const at = spaces.reduce((best, i) =>
    Math.abs(i - text.length / 2) < Math.abs(best - text.length / 2) ? i : best,
  )
  return [text.slice(0, at), text.slice(at + 1)]
}

export function layoutSlice(word: string, text: string, count: number): SliceLayout {
  // Half the slice's angle; past a half turn the slice is wider than the text can need.
  const half = Math.min(Math.PI / Math.max(1, count), Math.PI / 2.2)
  const widthAt = (r: number) => 2 * r * Math.tan(half) * FILL

  // Checked at its inner edge, where the slice is narrower than at its centre.
  const wordSize = Math.min(
    WORD_MAX,
    widthAt(WORD_C - WORD_MAX / 2) / Math.max(1, word.length * WORD_EM),
  )
  // A clear gap, so the word and the prize read as two things.
  const out = WORD_C - wordSize / 2 - 5.5

  const fit = (lines: string[]) => {
    const longest = Math.max(...lines.map((l) => l.length))
    for (let size = 14; size >= 3; size -= 0.25) {
      const inner = out - longest * EM * size
      if (inner < TEXT_IN) continue
      const height = size * (1 + (lines.length - 1) * LINE_GAP)
      if (height > widthAt(inner)) continue
      return { lines, size, inner, height }
    }
    return null
  }

  const one = fit([text])
  const split = halves(text)
  const two = split ? fit(split) : null
  const best =
    (two && (!one || two.size > one.size) ? two : one) ??
    ({ lines: [text], size: 3, inner: TEXT_IN, height: 3 } as const)

  return {
    word: { size: wordSize },
    lines: [...best.lines],
    size: best.size,
    lineY: best.lines.map((_, i) => -best.height / 2 + best.size / 2 + i * best.size * LINE_GAP),
    out,
    inner: best.inner,
  }
}
